import { defineStore } from 'pinia';
import { validateGlobalCca, validateGlobalAch } from '@/helpers/wallet';

/** Types */
import type { WalletStateType } from './types';
import type { WalletType, WalletBalance } from '@/types/WalletType';

/** Services */
import { walletService } from '@/shared/common/services/wallet.service';
import { useAccountStore } from '../account';
import { isUserProcess } from '@/helpers/kyc';
import { remunerationClpService } from '@/shared/common/services/remunerationClp.service';
import { logger } from '@/shared/common/utils/logger.util';

let setWalletsInFlight: Promise<boolean> | null = null;

export const useWalletStore = defineStore('wallet', {
  state: (): WalletStateType => ({
    wallets: [],
    isMainWalletRemunerated: false,
    isCheckingRemuneration: false,
    remunerationChecked: false,
    isCreateWalletDrawerOpen: false,
  }),
  getters: {
    getMainWalletBalance(): WalletBalance {
      const walletMain = this.wallets?.find((wallet) => wallet.isMainAccount);
      if (walletMain) {
        return walletMain.balance;
      } else {
        return {
          availableBalance: 0,
          totalBalance: 0,
          lockedBalance: 0,
          currency: '',
          currencySymbol: '',
          dateMillis: 0,
          countryCode: '',
        };
      }
    },
    getMainWallet(): WalletType {
      const walletMain = this.wallets?.find((wallet) => wallet.isMainAccount);
      if (walletMain) {
        return walletMain;
      } else {
        return {} as WalletType;
      }
    },
    isGlobalCard(): boolean {
      return validateGlobalCca(this.getMainWallet);
    },
    isGlobalSedpeAccount(): boolean {
      return validateGlobalAch(this.getMainWallet);
    },
    getWalletById() {
      return (walletId: number) => {
        return this.wallets.find(
          (item) => item.walletId === walletId
        );
      }
    },
  },
  actions: {
    /**
     * Carga las wallets del usuario desde walletService y actualiza el state.
     * Absorbe el fold internamente. El retorno indica si la carga fue exitosa
     * para que los callers puedan diferenciar entre "sin wallets" y "fallo de carga".
     * Los callers leen el state reactivo (walletStore.wallets) con storeToRefs.
     * @returns Promise<boolean> — true si la carga fue exitosa, false si falló o no se ejecutó.
     */
    async setWallets(): Promise<boolean> {
      const accountStore = useAccountStore();
      if (isUserProcess(accountStore.kycStatus.b2bUser)) {
        return false;
      }

      if (setWalletsInFlight) {
        return setWalletsInFlight;
      }

      setWalletsInFlight = (async (): Promise<boolean> => {
        let didSucceed = false;
        try {
          const result = await walletService.getWallets();
          result.fold(
            (error) => {
              logger.warn('Failed to load wallets', {
                code: error.code,
                status: error.status,
                reason: error.reason,
              });
            },
            (success) => {
              didSucceed = true;
              if (success.data) {
                this.wallets = success.data.sort((a: WalletType, b: WalletType) => {
                  /**
                   * Wallets principales van primero
                   */
                  if (a.isMainAccount && !b.isMainAccount) return -1;
                  if (!a.isMainAccount && b.isMainAccount) return 1;

                  /**
                   * Wallets con cuentas bancarias van después de la wallet principal. Ordenados por el accountId
                   */
                  if (a.bankAccounts.length && b.bankAccounts.length) {
                    return a.walletId - b.walletId;
                  } else if (a.bankAccounts.length) {
                    return -1;
                  } else if (b.bankAccounts.length) {
                    return 1;
                  }

                  /**
                   * Otras wallets secundarias van último
                   */
                  return 0;
                });

                const mainWallet = this.getMainWallet;
                if (
                  mainWallet.walletId &&
                  !this.remunerationChecked &&
                  !this.isCheckingRemuneration
                ) {
                  this.checkMainWalletRemuneration();
                }
              }
            }
          );
        } finally {
          setWalletsInFlight = null;
        }
        return didSucceed;
      })();

      return setWalletsInFlight;
    },
    /**
     * Verifica si la wallet principal tiene remuneración CLP
     * @param {boolean} force - Si es true, fuerza una nueva verificación incluso si ya se verificó previamente
     */
    async checkMainWalletRemuneration(force = false): Promise<void> {
      if (this.isCheckingRemuneration) {
        return;
      }

      if (this.remunerationChecked && !force) {
        return;
      }

      const mainWallet = this.getMainWallet;
      if (!mainWallet.walletId) {
        return;
      }


      const result = await remunerationClpService.getRemunerationClpData(
        mainWallet.walletId.toString()
      );

      result.fold(
        () => {
          this.isMainWalletRemunerated = false;
        },
        (success) => {
          this.isMainWalletRemunerated = success.data;
        }
      );

      this.remunerationChecked = true;
    },
    setIsMainWalletRemunerated(value: boolean): void {
      this.isMainWalletRemunerated = value;
    },
    openCreateWalletDrawer(): void {
      this.isCreateWalletDrawerOpen = true;
    },
    closeCreateWalletDrawer(): void {
      this.isCreateWalletDrawerOpen = false;
    },
  },
});
